// ── VenueCredStatus — always-on per-venue credential/connection dots ──
//
// Three tiny K / P / US pills with a status dot each, for the persistent chrome
// (KeyModeBanner strip + StatusBar footer). Now that a venue outage
// DEGRADES instead of blocking (Jul-9 Kalshi-maintenance lesson), the UI
// needs an always-visible answer to "which venue is actually up right
// now" — quiet enough to ignore, present enough to notice when a dot
// isn't green (Brendan, Jul-9).
//
// States:
//   Kalshi — green: connected with a live balance read;
//            amber: connected but the balance probe failed (degraded —
//                   e.g. the Thursday 3–5am ET maintenance pause);
//            red:   credential/connection error;
//            gray:  connecting / not configured.
//   Poly   — green: org pool (or personal connection) answering;
//            amber: relay answered "no poly path" (unconfigured/geoblock);
//            gray:  still resolving.
//   US     — green: an enabled, ready Polymarket US account (R4-F1);
//            amber: account present but not ready (disabled key, service
//                   not running) or the latest rail read failed (stale);
//            red:   the read failed;
//            gray:  no US account for this login / resolving.
//            Fed by reads the page already makes (useUsVenueStatus): no
//            poll of its own.

import { useAccountStore } from '@/stores/accountStore';
import { ORG_BALANCE_HIDDEN_TITLE } from '@/lib/orgBalanceHidden';
import { usePolyBalance } from '@/hooks/usePolyBalance';
import { useUsVenueStatus } from '@/hooks/useUsVenueStatus';

type Tone = 'green' | 'amber' | 'red' | 'gray';

const DOT: Record<Tone, string> = {
  green: 'bg-green-500',
  amber: 'bg-amber-400',
  red: 'bg-red-500',
  gray: 'bg-gray-500',
};

function Pill({ label, tone, title, testId }: { label: string; tone: Tone; title: string; testId?: string }) {
  return (
    <span
      className="text-muted-foreground inline-flex items-center gap-1 font-mono text-[10px]"
      title={title}
      data-testid={testId}
    >
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${DOT[tone]}`} />
      {label}
    </span>
  );
}

export function VenueCredStatus() {
  const status = useAccountStore((s) => s.status);
  const balance = useAccountStore((s) => s.balance);
  const balanceHidden = useAccountStore((s) => s.balanceHidden);
  const keyMode = useAccountStore((s) => s.keyMode);
  const poly = usePolyBalance();
  const us = useUsVenueStatus();

  let kalshiTone: Tone;
  let kalshiTitle: string;
  // #1205: a hidden balance is the relay ANSWERING with a permission state —
  // a healthy connection, never "degraded".
  if (status === 'connected' && (balance !== null || balanceHidden)) {
    kalshiTone = 'green';
    kalshiTitle = `Kalshi connected (${keyMode === 'org' ? 'org keys' : 'personal keys'})`
      + (balanceHidden ? `. ${ORG_BALANCE_HIDDEN_TITLE}` : '');
  } else if (status === 'connected') {
    kalshiTone = 'amber';
    kalshiTitle =
      'Kalshi degraded — connected but the balance read is failing. Kalshi pauses trading Thu 3:00–5:00 AM ET for maintenance; Polymarket is unaffected.';
  } else if (status === 'error') {
    kalshiTone = 'red';
    kalshiTitle = 'Kalshi connection error — check Settings (or the Thu 3–5 AM ET maintenance window)';
  } else {
    kalshiTone = 'gray';
    kalshiTitle = status === 'connecting' ? 'Kalshi connecting…' : 'Kalshi not connected';
  }

  let polyTone: Tone;
  let polyTitle: string;
  if (poly.source !== null) {
    polyTone = 'green';
    polyTitle = `Polymarket answering (${poly.source === 'org' ? 'org keys' : 'personal keys'})`
      + (poly.hidden ? `. ${ORG_BALANCE_HIDDEN_TITLE}` : '');
  } else if (poly.unavailable) {
    polyTone = 'amber';
    polyTitle =
      'Polymarket unavailable — no org poly keys on this server and no personal connection (or the relay egress is geoblocked)';
  } else {
    polyTone = 'gray';
    polyTitle = 'Polymarket status resolving…';
  }

  return (
    <span className="inline-flex items-center gap-2.5">
      <Pill label="K" tone={kalshiTone} title={kalshiTitle} />
      <Pill label="P" tone={polyTone} title={polyTitle} />
      <Pill label="US" tone={us.tone} title={us.title} testId="venue-cred-us" />
    </span>
  );
}
